// Logs Core Web Vitals to the console so we can compare runs in DevTools.
// Uses native PerformanceObserver — no dep upgrade required and captures INP
// (which the bundled web-vitals 2.x predates).
const log = (metric, value, extra = '') => {
  const colors = { LCP: '#10b981', CLS: '#3b82f6', INP: '#f59e0b', FCP: '#8b5cf6', TTFB: '#06b6d4' };
  const color = colors[metric] || '#888';
  // eslint-disable-next-line no-console
  console.log(`%c[CWV] ${metric}: ${typeof value === 'number' ? value.toFixed(2) : value}${extra ? ' (' + extra + ')' : ''}`, `color:${color};font-weight:bold`);
};

const reportWebVitals = (onPerfEntry) => {
  if (typeof window === 'undefined' || typeof PerformanceObserver === 'undefined') return;

  const dispatch = (name, value, extra) => {
    log(name, value, extra);
    if (typeof onPerfEntry === 'function') {
      onPerfEntry({ name, value, extra });
    }
  };

  // LCP — last entry is the final LCP candidate
  try {
    let lcpValue = 0;
    const lcpObserver = new PerformanceObserver((list) => {
      const entries = list.getEntries();
      const last = entries[entries.length - 1];
      lcpValue = last.renderTime || last.loadTime || last.startTime;
    });
    lcpObserver.observe({ type: 'largest-contentful-paint', buffered: true });
    // Flush LCP on hidden/pagehide (matches web-vitals semantics)
    const flushLCP = () => {
      if (document.visibilityState === 'hidden' && lcpValue) {
        dispatch('LCP', lcpValue, 'ms');
        document.removeEventListener('visibilitychange', flushLCP);
      }
    };
    document.addEventListener('visibilitychange', flushLCP);
    // Also flush after 5s of idle for dev visibility
    setTimeout(() => lcpValue && dispatch('LCP', lcpValue, 'ms'), 5000);
  } catch (e) { /* unsupported */ }

  // CLS — cumulative score over session windows
  try {
    let clsValue = 0;
    new PerformanceObserver((list) => {
      for (const entry of list.getEntries()) {
        if (!entry.hadRecentInput) clsValue += entry.value;
      }
    }).observe({ type: 'layout-shift', buffered: true });
    setTimeout(() => dispatch('CLS', clsValue), 5000);
  } catch (e) { /* unsupported */ }

  // INP — worst interaction duration (replaces FID)
  try {
    let inpValue = 0;
    new PerformanceObserver((list) => {
      for (const entry of list.getEntries()) {
        if (entry.duration > inpValue) {
          inpValue = entry.duration;
        }
      }
    }).observe({ type: 'event', durationThreshold: 40, buffered: true });
    // Sample after interactions are likely captured
    setTimeout(() => inpValue && dispatch('INP', inpValue, 'ms'), 8000);
  } catch (e) { /* unsupported, durationThreshold may be unrecognized */ }

  // FCP + TTFB from Navigation Timing
  try {
    new PerformanceObserver((list) => {
      for (const entry of list.getEntries()) {
        if (entry.name === 'first-contentful-paint') {
          dispatch('FCP', entry.startTime, 'ms');
        }
      }
    }).observe({ type: 'paint', buffered: true });
  } catch (e) { /* unsupported */ }

  try {
    const nav = performance.getEntriesByType('navigation')[0];
    if (nav) dispatch('TTFB', nav.responseStart, 'ms');
  } catch (e) { /* unsupported */ }
};

export default reportWebVitals;
